import { z } from "zod";
import { FinancingStatus, FinancingStatusSchema } from "@remi/enums/financing-status";
import { type FinancingVendor, FinancingVendorSchema } from "@remi/enums/financing-vendor";
import type { FundingMilestone } from "@remi/enums/funding-milestone";
import type { FundingMilestoneSchedule } from "@remi/enums/funding-milestone-schedule";
import { isOneOf } from "@remi/ts-utils";

import type { StateType } from "./state";

/**
 * A short-lived bearer token for a financing vendor's API plus the moment it should be
 * treated as expired. Structurally identical to the SDK-layer token shapes (e.g.
 * `SunlightToken` in `@remi/sdk-sunlight`); kept vendor-agnostic here so the cross-vendor
 * `FinancingPackage` contract never depends on a single vendor's SDK.
 */
interface FinancingToken {
  accessToken: string;
  expiresAt: Date;
}

/**
 * The cache seam threaded into every `FinancingPackage` method. domain-services builds the
 * DB-backed implementation (reading/writing `ExternalApiToken`) and passes it at each call
 * site, so the package layer never touches `@remi/repositories`/`@remi/db`. Vendors that don't
 * authenticate (e.g. GoodLeap) accept and ignore it. Structurally identical to the SDK's
 * `SunlightTokenProvider`, so a vendor SDK provider is assignable here without a cast.
 */
export interface FinancingTokenProvider {
  getToken: () => Promise<FinancingToken | null>;
  saveToken: (token: FinancingToken) => Promise<void>;
  /**
   * Expire a rejected token's row in place so the next read doesn't serve it before re-minting.
   * Takes the raw access token — the row is keyed by its hash; the new expiry is the provider's concern.
   */
  invalidateToken: (accessToken: string) => Promise<void>;
}

/**
 * No-op provider for vendors that don't authenticate (GoodLeap/placeholder). The contract requires
 * a provider, but resolving the real `SUNLIGHT_TOKEN_PROVIDER` for a non-Sunlight vendor would issue
 * a wasted `ExternalApiToken` read — pass this instead. Single shared instance so call sites don't
 * re-declare it and drift if `FinancingTokenProvider` gains a method.
 */
export const inertFinancingTokenProvider: FinancingTokenProvider = {
  getToken: async () => null,
  saveToken: async () => {},
  invalidateToken: async () => {},
};

export interface RecordFinancingWebhookEventInput {
  vendor: FinancingVendor;
  eventName: string | null;
  lenderLoanId: string | null;
  lenderEventAt: Date | null;
  receivedAt: Date;
  deliveryKey: string | null;
  body: unknown;
}

export interface UpsertFinancingInput {
  externalId: string | null;
  bidId: number;
  requestForQuoteId: number;
  homeownerInfo: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  propertyAddress: {
    street: string;
    city: string;
    stateCode: StateType;
    zip: string;
  };
  financingInfo: {
    loanAmount: number;
    apr?: number;
    termMonths?: number;
    productType?: string;
  };
  salesPerson: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  isStrategicPartner?: boolean;
}

export interface UpsertFinancingReturn {
  externalId: string;
  status: FinancingStatus;
  apr: number;
  termMonths: number;
  loanAmount: number;
}

export type UpsertFinancingFn = (
  input: UpsertFinancingInput,
  tokenProvider: FinancingTokenProvider,
) => Promise<UpsertFinancingReturn>;

interface FinalizeFinancingInput {
  externalId: string;
  projectCost: number;
}

interface FinalizeFinancingReturn {
  success: boolean;
}

export type FinalizeFinancingFn = (
  input: FinalizeFinancingInput,
  tokenProvider: FinancingTokenProvider,
) => Promise<FinalizeFinancingReturn>;

interface GetFinancingInput {
  externalId: string;
}
/**
 *   id          String    @id @default(cuid())
  financingId String    @unique
  financing   Financing @relation(fields: [financingId], references: [id])
  name        String
  email       String
  phone       String
 */

interface Applicant {
  name: string;
  email?: string;
  phone: string;
}

interface CoSigner extends Applicant {
  email: string;
}

type LoanStip = {
  name: string;
  status: string;
  description: string;
  longDescription?: string;
  shortName?: string;
  folderName?: string;
  notes?: string;
  installerOnlyEmail?: boolean;
};

export type GetFinancingReturn = Partial<{
  externalId: string;
  loanAmount: number;
  disbursedAmount: number;
  apr: number;
  termMonths: number;
  productType: string;
  productName: string;
  isACH: boolean;
  maxQualifiedAmount: number;
  maxApprovedAmount: number;
  isPlaceholder: boolean;
  // null is the vendor reporting a cleared date (a voided signature); undefined is "not reported".
  loanDocumentsSignedAt: Date | null;
  finalizedDate: Date | null;
  creditExpirationDate: Date;
}> & {
  applicant?: Applicant | null;
  coSigner?: CoSigner | null;
  status?: FinancingStatus;
  stips?: LoanStip[];
};

export type GetFinancingFn = (
  input: GetFinancingInput,
  tokenProvider: FinancingTokenProvider,
) => Promise<GetFinancingReturn>;

interface SendLoanDocsInput {
  externalId: string;
}

interface SendLoanDocsReturn {
  success: boolean;
}

export type SendLoanDocsFn = (
  input: SendLoanDocsInput,
  tokenProvider: FinancingTokenProvider,
) => Promise<SendLoanDocsReturn>;

interface ClaimMilestoneInput {
  externalId: string;
  milestone: FundingMilestone;
  fundingMilestoneSchedule: FundingMilestoneSchedule;
}

export type ClaimMilestoneReturn = { success: boolean } | { notApplicable: true };

export type ClaimMilestoneFn = (
  input: ClaimMilestoneInput,
  tokenProvider: FinancingTokenProvider,
) => Promise<ClaimMilestoneReturn>;

interface GetExternalUrlInput {
  externalId?: string;
}

export type GetExternalUrlFn = (
  input: GetExternalUrlInput,
  tokenProvider: FinancingTokenProvider,
) => Promise<string> | string;

interface UploadFilesInput {
  externalId: string;
  files: { id: string; name: string }[];
  areaSquaresNoWaste: number;
}

/** @knipIgnore — declaration emit: dependents' emitted .d.ts name this type, which knip can't see */
export interface UploadFilesReturn {
  success: boolean;
}

export type UploadFilesFn = (
  input: UploadFilesInput,
  tokenProvider: FinancingTokenProvider,
) => Promise<UploadFilesReturn>;

type GetLoanOptionsInput =
  | { loanId: string }
  | { apr: number; loanAmount: number; stateCode?: StateType; externalId: string };

export type LoanOption = {
  monthlyPayment: number;
  apr: number;
  termMonths: number;
  productType?: string;
};

type GetLoanOptionsReturn = LoanOption[];

export type GetLoanOptionsFn = (
  input: GetLoanOptionsInput,
  tokenProvider: FinancingTokenProvider,
) => Promise<GetLoanOptionsReturn>;

export type LoanAgreementSigningSession =
  | { status: "ready"; envelopeURL: string }
  | { status: "completed" }
  | { status: "pending" }
  | { status: "error" };

export interface FinancingPackage {
  upsert: UpsertFinancingFn;
  uploadFiles: UploadFilesFn;
  finalize: FinalizeFinancingFn;
  get: GetFinancingFn;
  sendLoanDocs: SendLoanDocsFn;
  claimMilestone: ClaimMilestoneFn;
  getExternalUrl: GetExternalUrlFn;
  displayName: FinancingVendor;
  getLoanOptions: GetLoanOptionsFn;
}

export const FinancingSchema = z.object({
  externalId: z.string(),
  vendor: FinancingVendorSchema,
  status: FinancingStatusSchema,
  loanAmount: z.number(),
});

const FinancingStatusCanChangeVendorValues = [
  FinancingStatus.DECLINED,
  FinancingStatus.CANCELLED,
  FinancingStatus.CREATED,
] as const satisfies FinancingStatus[];

export const canChangeFinancingVendor = (status: FinancingStatus | undefined) => {
  if (!status) {
    return true;
  }

  return isOneOf(status, FinancingStatusCanChangeVendorValues);
};
